import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { getBuildInfo } from '../../buildInfo';
import { fetchSystemStatus, SystemStatusResponse } from '../../services/status';
import './StatusPage.css';

export function StatusPage() {
  const { t } = useLanguage();
  const build = useMemo(() => getBuildInfo(), []);
  const [status, setStatus] = useState<SystemStatusResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function run() {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchSystemStatus();
        if (!cancelled) {
          setStatus(data);
        }
      } catch (e: any) {
        if (!cancelled) {
          setError(e?.message || String(e));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    run();
    return () => {
      cancelled = true;
    };
  }, []);

  const llmLine = status?.llm
    ? `${status.llm.provider || 'unknown'} · ${status.llm.model || 'unknown'}`
    : 'n/a';

  return (
    <div className="statusRoot">
      <div className="statusCard">
        <div className="statusHeader">
          <h2 className="statusTitle">{t('status.title')}</h2>
          <div className="statusLinks">
            <Link to="/dashboard" className="statusLink">
              {t('nav.dashboard')}
            </Link>
            <Link to="/login" className="statusLink">
              {t('auth.login')}
            </Link>
          </div>
        </div>

        <div className="statusBuild">
          {t('status.build')}: v{build.version} · {build.branch}{build.sha ? `@${build.sha}` : ''}
        </div>

        <hr className="statusHr" />

        {loading && <div>{t('status.loadingStatus')}</div>}
        {error && (
          <div className="statusError">{error}</div>
        )}

        {!loading && !error && (
          <>
            <div className="statusGrid">
              <div className="statusPanel">
                <div className="statusPanelTitle">LLM</div>
                <div>{t('status.configured')}: {llmLine}</div>
                <div>
                  {t('status.available')}: {String(status?.llm?.available)}
                  {typeof status?.llm?.availableMs === 'number' ? ` (${status.llm.availableMs}ms)` : ''}
                </div>
                {status?.llm?.ollamaBaseUrl ? <div>{t('status.ollamaUrl')}: {status.llm.ollamaBaseUrl}</div> : null}
                {status?.llm?.error ? <div className="statusTextError">{t('common.error')}: {status.llm.error}</div> : null}
                <div>{t('status.mockFallback')}: {String(status?.llm?.allowMockFallback)}</div>
              </div>

              <div className="statusPanel">
                <div className="statusPanelTitle">Firebase</div>
                <div className="statusSubtle">
                  {t('status.serverTime')}: {status?.now || 'n/a'}
                </div>

                <div className="statusSectionTitle">{t('status.emulatorEnv')}</div>
                <pre className="statusPre">
{JSON.stringify(status?.firebase?.emulatorEnv || {}, null, 2)}
                </pre>

                <div className="statusSectionTitleSpaced">{t('status.connectivityChecks')}</div>
                <pre className="statusPre">
{JSON.stringify(status?.firebase?.checks || {}, null, 2)}
                </pre>
              </div>

              <div className="statusPanel">
                <div className="statusPanelTitle">{t('status.rawResponse')}</div>
                <pre className="statusPre">
{JSON.stringify(status || {}, null, 2)}
                </pre>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
